// Shared parsers for the error envelope used by member-calls2 endpoints:
//
//   Message_v2   — string[] of translation keys (preferred)
//   error_ref    — optional correlation id appended to the list
//   error.body.error.message — raw fallback string
//
// The envelope shows up in two places:
//   1. Inside `response.data` of an axios error (network / 4xx)
//   2. Inside a successful 200 body when `{ success: false }` is returned
//
// Both variants return an array of keys ready to feed into `ErrorMessages`.
// Always returns at least one entry (falls back to `defaultKey`).

interface ErrorEnvelope {
    Message_v2?: string[]
    error_ref?: string
    message?: string
    error?: { body?: { error?: { message?: string } } }
}

const UUID_EXPIRED_MESSAGE = 'uuid expired'

const parseEnvelope = (data: ErrorEnvelope | undefined, defaultKey: string): string[] => {
    const msgV2 = data?.Message_v2
    const errorRef = data?.error_ref
    const fallback = data?.error?.body?.error?.message

    const msgs = msgV2?.length ? [...msgV2] : fallback ? [fallback] : [defaultKey]
    if (errorRef) msgs.push(errorRef)
    return msgs
}

/** Parse an axios error thrown from a proxiedPost/Put/Get call. */
export const parseOmanAxiosError = (e: unknown, defaultKey = 'network_error'): string[] => {
    const data = (e as { response?: { data?: ErrorEnvelope } })?.response?.data
    return parseEnvelope(data, defaultKey)
}

export const isOmanUuidExpiredError = (e: unknown): boolean => {
    const data = (e as { response?: { data?: ErrorEnvelope } })?.response?.data
    return data?.message?.trim().toLowerCase() === UUID_EXPIRED_MESSAGE
}

/** Parse a `{ success: false }` body returned by a successful HTTP response. */
export const parseOmanResultError = (result: ErrorEnvelope | Record<string, unknown> | null | undefined, defaultKey = 'checkout_error'): string[] =>
    parseEnvelope(result as ErrorEnvelope | undefined, defaultKey)
